import Box from "@mui/material/Box";
import MuiSwitch from "@mui/material/Switch";
import { alpha } from "@mui/material/styles";
import { useId } from "react";
import { tokens } from "../../design-system/tokens";

const switchSecondarySx = {
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: tokens.colors.secondary,
    "&:hover": {
      backgroundColor: alpha(tokens.colors.secondary, 0.08),
    },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: tokens.colors.secondaryLight,
    opacity: 1,
  },
  "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": {
    color: alpha(tokens.colors.secondary, 0.5),
  },
  "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track": {
    backgroundColor: alpha(tokens.colors.secondary, 0.5),
    opacity: 1,
  },
} as const;

const labelTypographyBySize = {
  small: tokens.typography.inputSmall,
  medium: tokens.typography.inputMedium,
} as const;

type SwitchProps = {
  name?: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: "small" | "medium";
  className?: string;
};

export default function Switch({
  name,
  label,
  checked,
  onChange,
  disabled = false,
  size = "small",
  className,
}: SwitchProps) {
  const generatedId = useId();
  const switchId = name ? `${name}-${generatedId}` : generatedId;
  const labelTypography = labelTypographyBySize[size];
  const labelColor = disabled ? tokens.colors.text.tertiary : tokens.colors.text.primary;

  return (
    <label
      htmlFor={switchId}
      className={className}
      style={{
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 0,
        margin: 0,
        direction: "ltr",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Box
        component="span"
        sx={{
          margin: "0 !important",
          fontFamily: `${labelTypography.fontFamily} !important`,
          fontSize: `${labelTypography.fontSize} !important`,
          fontWeight: `${labelTypography.fontWeight} !important`,
          lineHeight: `${String(labelTypography.lineHeight)} !important`,
          color: `${labelColor} !important`,
        }}
      >
        {label}
      </Box>
      <MuiSwitch
        id={switchId}
        name={name}
        checked={checked}
        onChange={(_event, nextChecked) => onChange(nextChecked)}
        disabled={disabled}
        size={size}
        sx={switchSecondarySx}
        slotProps={{ input: { "aria-label": label } }}
      />
    </label>
  );
}
